import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import NetworkUnsupportedModal from "../../Modules/NetworkUnsupportedModal";
import PreflightModalTransfer from "../../Modules/PreflightModalTransfer";
import AddressInput from "../Custom/AddressInput";
import TransferActiveModal from "../../Modules/TransferActiveModal";
import { useAccount, useChainId, useSwitchChain } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import {
  NFTTokenDeclaration,
  useChainbridge,
} from "../../Contexts/ChainbridgeContext";
import NFTTokenSelectInput from "../Custom/NFTTokenSelectInput";
import NFTTokenIdSelectInput from "../Custom/NFTTokenIdSelectInput";
import { useTokenBalances } from "../../hooks/useTokenBalances";

import { ethers, utils } from "ethers";
import { chainbridgeConfig } from "../../chainbridgeConfig";
import Fees from "../Custom/Fees";
import PageHeading from "../UI/PageHeading";
import {
  Heading,
  Text,
  Field,
  Box,
  Stack,
  Flex,
  Button,
  Alert,
  Image,
  Separator,
  VStack,
  Link,
} from "@chakra-ui/react";
import { toaster } from "../../config/toaster";
import { ExternalLink, Clock } from "lucide-react";
import NFTDisplay from "../Custom/NFTDisplay";
import CurrentGasFees from "../Custom/CurrentGasFees";
import { toastSuccess, toastError } from "../../config/toast";
import PATCH from "../../media/logos/patch.png";
import CONSENSYS from "../../media/logos/consensys.svg";
import { getFeatureFlagStatus } from "../../featureFlags";

const shadowForkMessagesEnabled = getFeatureFlagStatus(
  "shadowForkMessagesEnabled"
);

type PreflightDetails = {
  tokenId?: string;
  token?: NFTTokenDeclaration;
  receiver?: string;
};

const NFTTransferPage = () => {
  const { address, isConnected: isReady } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const {
    homeChain,
    destinationChain,
    deposit,
    transactionStatus,
    resetDeposit,
    bridgeFee,
    homeChainIsPalm,
    palmRelayFee,
    palmGreenFee,
    tollTokenAddress,
    tollTokenSymbol,
    tollTokenDecimals,
    nftTokens,
    updateTokenIdsOwnedForNFT,
    ownedTokenIdsByTokenAddress,
    updateMetadataForTokenId,
    tokenMetadataByTokenAddressAndId,
  } = useChainbridge();

  // Only needed for toll token balance check in Yup schema
  const tollTokenConfigs =
    homeChain?.tokens.filter((t) => t.address === tollTokenAddress) ?? [];
  const tokensMap = useTokenBalances(tollTokenConfigs);

  const [walletConnecting] = useState(false);
  const [preflightModalOpen, setPreflightModalOpen] = useState<boolean>(false);
  const [switchingNetworks, setSwitchingNetworks] = useState<boolean>(false);

  const [preflightDetails, setPreflightDetails] = useState<PreflightDetails>({
    receiver: "",
    token: undefined,
    tokenId: "",
  });

  useEffect(() => {
    if (!preflightDetails.token) return;
    updateTokenIdsOwnedForNFT(preflightDetails.token.contract);
  }, [preflightDetails.token, updateTokenIdsOwnedForNFT]);

  useEffect(() => {
    if (!preflightDetails.token || !preflightDetails.tokenId) return;
    updateMetadataForTokenId(
      preflightDetails.token.contract,
      preflightDetails.tokenId
    );
  }, [
    preflightDetails.token,
    preflightDetails.tokenId,
    updateMetadataForTokenId,
  ]);

  const handleConnect = () => {
    openConnectModal?.();
  };

  const totalFees = () => {
    const relayFee = homeChainIsPalm
      ? parseInt(ethers.utils.formatUnits(palmRelayFee, tollTokenDecimals))
      : bridgeFee;
    const greenFee = homeChainIsPalm
      ? parseInt(ethers.utils.formatUnits(palmGreenFee, tollTokenDecimals))
      : 0;
    return relayFee ? relayFee + greenFee : greenFee;
  };

  const transferSchema = z.object({
    tokenId: z
      .string()
      .min(1, "Please set a value")
      .refine(
        (value) =>
          !!value &&
          preflightDetails.token &&
          ownedTokenIdsByTokenAddress[preflightDetails.token.address]
            ?.map((bigNumber) => bigNumber.toString())
            .includes(value),
        "Please select an NFT"
      ),
    token: z.string().min(1, "Please select a token"),
    receiver: z
      .string()
      .min(1, "Please add a receiving address")
      .refine((value) => utils.isAddress(value), "Please add a valid address")
      .refine((_value) => {
        const bridgeFees = totalFees();
        if (bridgeFees == 0 || !homeChainIsPalm) return true;
        if (
          tollTokenAddress &&
          tokensMap[tollTokenAddress] &&
          tokensMap[tollTokenAddress]!.balance
        ) {
          return bridgeFees <= tokensMap[tollTokenAddress]!.balance;
        }
        return false;
      }, "Insufficient funds to pay bridge fees"),
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm<{
    token: string;
    tokenId: string;
    receiver: string;
  }>({
    mode: "onBlur",
    resolver: zodResolver(transferSchema),
  });

  const resetForm = async () => {
    setPreflightDetails({ receiver: "", token: undefined, tokenId: "" });
    reset({ token: "" });
  };

  const handleSwitchNetwork = async (
    chainName?: string,
    networkId?: number
  ) => {
    const id = networkId || destinationChain?.networkId;
    const name = (networkId && chainName) || destinationChain?.name;

    const showToast = (success: boolean) => {
      const title = success
        ? `Chain changed to ${name}`
        : `Sorry, there was an error changing chains`;
      const conf = success ? toastSuccess : toastError;
      return toaster.create({
        title,
        type: conf.status,
        duration: conf.duration,
      });
    };

    if (id) {
      setSwitchingNetworks(true);
      switchChainAsync({ chainId: id })
        .then(() => showToast(true))
        .catch(() => showToast(false))
        .finally(() => setSwitchingNetworks(false));
    }
  };

  return (
    <>
      <PageHeading title="NFT Bridge" />
      {shadowForkMessagesEnabled && (
        <Box mb="16px" pl="8px" color="palmNetwork.100" bg="palmNetwork.800">
          <Box pb="32px" pt="48px" maxW="728px" pl="16px">
            <Heading fontSize="24px" mb="16px">
              Shadow Fork Testnet testing
            </Heading>
            <Text mb="16px">
              This application has been updated so that the Palm-Goerli testnet
              bridge now interacts with the temporary &apos;shadow fork&apos;
              Palm Testnet. Bridging between Goerli and the official Palm
              Testnet is currently suspended whilst this testing is carried out.
            </Text>
            <Text mb="16px">
              To use this bridge when sending tokens from the shadow fork Palm
              Testnet to Goerli, you will need to update your wallet to use the
              shadow fork Palm Testnet RPC - this can be added from the
              Configure page.
            </Text>
            <Text mb="16px">
              Please refer to the Palm network documentation for details at{" "}
              <Link>https://docs.palm.network/network-update</Link>
            </Text>
          </Box>
        </Box>
      )}

      <Box
        width="540px"
        bg="white"
        border="1px solid"
        borderColor="brand.300"
        p="32px"
        ml="18px"
        mt="8px"
      >
        {!isReady ? (
          <Button onClick={handleConnect}>Connect Wallet</Button>
        ) : walletConnecting ? (
          <p>
            This app requires access to your wallet, <br />
            please login and authorize access to continue.
          </p>
        ) : (
          <>
            <NetworkDisplay location="Origin Network" name={homeChain?.name} />
            <Button
              width="0px"
              mt="8px"
              mb="10px"
              onClick={() => handleSwitchNetwork()}
              disabled={switchingNetworks}
            >
              ↑↓
            </Button>
            <NetworkDisplay
              location="Destination network"
              name={destinationChain?.name}
            />
          </>
        )}
        <form onSubmit={handleSubmit(() => setPreflightModalOpen(true))}>
          <Stack gap="50px" direction="column" mt="34px">
            <Stack gap="24px" direction="row">
              <Field.Root invalid={!!errors?.token?.message} required>
                <Field.Label>Token</Field.Label>
                <NFTTokenSelectInput
                  nftTokens={nftTokens}
                  name="token"
                  isDisabled={!destinationChain}
                  sync={(selectedToken: NFTTokenDeclaration) => {
                    // Clear any id selected for the previous collection; keeping
                    // it would fetch metadata for an id that doesn't exist on the
                    // newly-selected token and revert.
                    setPreflightDetails({
                      ...preflightDetails,
                      token: selectedToken,
                      tokenId: "",
                    });
                    setValue("tokenId", "");
                  }}
                  register={register}
                  setValue={setValue}
                  value={getValues("token")}
                  options={
                    nftTokens.map((declaration, index) => ({
                      value: declaration.address,
                      label: (
                        <div>
                          <span>{nftTokens[index].name}</span>
                        </div>
                      ),
                    })) || []
                  }
                />
                <Field.ErrorText>{errors?.token?.message}</Field.ErrorText>
              </Field.Root>
              <Field.Root invalid={!!errors?.tokenId?.message} required>
                <Field.Label>NFT ID</Field.Label>
                <NFTTokenIdSelectInput
                  selectedTokenIds={
                    ownedTokenIdsByTokenAddress[
                      preflightDetails.token?.address || ""
                    ] || []
                  }
                  selectedTokenId={preflightDetails.tokenId}
                  name="tokenId"
                  value={getValues("tokenId")}
                  isDisabled={!destinationChain}
                  sync={(tokenIdAsString) => {
                    setPreflightDetails({
                      ...preflightDetails,
                      tokenId: tokenIdAsString,
                    });
                  }}
                  register={register}
                  setValue={setValue}
                />
                <Field.ErrorText>{errors?.tokenId?.message}</Field.ErrorText>
              </Field.Root>
            </Stack>
            <NFTDisplay
              metadata={
                tokenMetadataByTokenAddressAndId[
                  preflightDetails.token?.address || ""
                ]
                  ? tokenMetadataByTokenAddressAndId[
                      preflightDetails.token?.address || ""
                    ][preflightDetails.tokenId || ""]
                  : undefined
              }
            />

            <Field.Root invalid={!!errors?.receiver?.message} required>
              <Field.Label>Destination address</Field.Label>
              <AddressInput
                name="receiver"
                isDisabled={!destinationChain}
                placeholder="Please enter the receiving address"
                senderAddress={`${address}`}
                value={preflightDetails.receiver || ""}
                onChange={(value: string) => {
                  setPreflightDetails({
                    ...preflightDetails,
                    receiver: value,
                  });
                }}
                register={register}
                setValue={setValue}
              />
              <Field.ErrorText>{errors?.receiver?.message}</Field.ErrorText>
            </Field.Root>
          </Stack>
          {homeChainIsPalm ? (
            <Alert.Root status="info" mt={4} mb={2}>
              <Clock size={16} style={{ marginRight: "8px" }} />
              <Alert.Content>
                <Alert.Description>
                  Minimum transfer time: 1 hour (256 blocks)
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          ) : null}
          <Box>
            <CurrentGasFees />
          </Box>
          <Fees
            relayFee={
              homeChainIsPalm
                ? parseInt(
                    ethers.utils.formatUnits(palmRelayFee, tollTokenDecimals)
                  )
                : bridgeFee
            }
            relayFeeSymbol={
              homeChainIsPalm ? tollTokenSymbol : homeChain?.nativeTokenSymbol
            }
            showGreenFee={homeChainIsPalm}
            greenFee={
              homeChainIsPalm
                ? parseInt(
                    ethers.utils.formatUnits(palmGreenFee, tollTokenDecimals)
                  )
                : undefined
            }
            greenFeeSymbol={homeChainIsPalm ? tollTokenSymbol : undefined}
            showTransferAmount={false}
          />
          <Flex justifyContent="center">
            <Button
              type="submit"
              disabled={!!errors.receiver || !!errors.token || !!errors.tokenId}
            >
              Start transfer
            </Button>
          </Flex>
        </form>
        <NetworkUnsupportedModal
          open={!homeChain && isReady}
          network={chainId}
          supportedNetworks={chainbridgeConfig.chains.map((bc) => bc.networkId)}
        />
        <PreflightModalTransfer
          open={preflightModalOpen}
          close={() => setPreflightModalOpen(false)}
          receiver={preflightDetails?.receiver || ""}
          sender={address || ""}
          start={() => {
            setPreflightModalOpen(false);
            if (preflightDetails.token && preflightDetails.receiver) {
              deposit({
                tokenAddress: preflightDetails.token.address,
                tokenId: preflightDetails.tokenId,
                recipient: preflightDetails.receiver,
              });
            }
          }}
          sourceNetwork={homeChain?.name || ""}
          targetNetwork={destinationChain?.name || ""}
          tokenSymbol={preflightDetails.token?.symbol || ""}
          tokenId={preflightDetails?.tokenId || ""}
        />
        <TransferActiveModal
          open={!!transactionStatus}
          close={() => {
            resetDeposit();
            resetForm();
          }}
        />
        <Separator borderColor="palmNetwork.100" mt="24px" />
        <Stack
          gap="22px"
          direction="column"
          align="left"
          width="540px"
          mt="36px"
        >
          <Stack gap="8px" direction="row" align="center">
            <Text fontSize="xs">Bridge operator: </Text>
            <Image src={CONSENSYS} height="32px" alt="Consensys" />
            <Text fontSize="xs">Carbon offsets: </Text>
            <Image src={PATCH} width="64px" alt="Patch" />
          </Stack>
          <VStack gap="1px" align="left">
            <Text fontSize="xs">
              <Link
                fontSize="xs"
                href={"https://consensys.net/privacy-policy/"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy &nbsp;
                <ExternalLink size={14} />
              </Link>{" "}
              |{" "}
              <Link
                fontSize="xs"
                href={"https://consensys.net/terms-of-use/"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use &nbsp;
                <ExternalLink size={14} />
              </Link>
            </Text>
          </VStack>
        </Stack>
      </Box>
    </>
  );
};
export default NFTTransferPage;

interface INetworkDisplay {
  location: string;
  name: string | undefined;
}

const NetworkDisplay: React.FC<INetworkDisplay> = ({
  location,
  name,
}: INetworkDisplay) => {
  return (
    <>
      <Text fontSize="12px" mb="4px">
        {location}
      </Text>
      <Heading fontSize="20px">{name}</Heading>
    </>
  );
};
